import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { 
  Button, 
  Tile, 
  Tag
} from '@carbon/react';
import {
  ArrowLeft,
  UserAvatar,
  Document,
  Download,
  CheckmarkFilled,
  WarningFilled,
  CloseFilled,
  Calendar,
  Currency,
  Package as PackageIcon,
  Wallet,
  ChevronDown,
  ChevronUp
} from '@carbon/icons-react';
import PageContainer from '../components/PageContainer';

const ResidentFinances = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isDarkMode = theme === 'g100';

  const [expandedSections, setExpandedSections] = useState({
    contractInfo: false,
    paymentHistory: false,
    invoices: false,
    pocketMoney: false
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Mock data - replace with real data
  const residentName = "Milica Petrović";
  
  const contractData = {
    contractNumber: "UGV-2024-00156",
    packageType: "Premium",
    startDate: "15.01.2024",
    endDate: "14.01.2025",
    pricePerDay: "4,500 RSD",
    pricePerMonth: "135,000 RSD",
    paymentStatus: "aktivna",
    nextPaymentDue: "15.11.2024",
    description: "Premium paket uključuje: privatnu sobu, pojačanu negu, dodatne terapije, i prilagođenu ishranu"
  };

  const paymentHistory = [
    { id: 1, month: "Oktobar 2024", amount: "135,000 RSD", date: "15.10.2024", status: "plaćeno", method: "Prenos" },
    { id: 2, month: "Septembar 2024", amount: "135,000 RSD", date: "15.09.2024", status: "plaćeno", method: "Prenos" },
    { id: 3, month: "Avgust 2024", amount: "135,000 RSD", date: "18.08.2024", status: "plaćeno", method: "Ček" },
    { id: 4, month: "Jul 2024", amount: "135,000 RSD", date: "15.07.2024", status: "plaćeno", method: "Prenos" },
    { id: 5, month: "Jun 2024", amount: "135,000 RSD", date: "20.06.2024", status: "plaćeno", method: "Prenos" },
    { id: 6, month: "Maj 2024", amount: "135,000 RSD", date: "15.05.2024", status: "plaćeno", method: "Prenos" }
  ];

  const invoices = [
    { id: 1, number: "INV-2024-1056", date: "01.10.2024", period: "Oktobar 2024", amount: "135,000 RSD", status: "plaćeno" },
    { id: 2, number: "INV-2024-0987", date: "01.09.2024", period: "Septembar 2024", amount: "135,000 RSD", status: "plaćeno" },
    { id: 3, number: "INV-2024-0891", date: "01.08.2024", period: "Avgust 2024", amount: "135,000 RSD", status: "plaćeno" },
    { id: 4, number: "INV-2024-0765", date: "01.07.2024", period: "Jul 2024", amount: "135,000 RSD", status: "plaćeno" },
    { id: 5, number: "INV-2024-0654", date: "01.06.2024", period: "Jun 2024", amount: "135,000 RSD", status: "plaćeno" },
    { id: 6, number: "INV-2024-0543", date: "01.05.2024", period: "Maj 2024", amount: "135,000 RSD", status: "plaćeno" }
  ];

  const pocketMoneyData = {
    balance: "12,500 RSD",
    totalDeposits: "45,000 RSD",
    totalSpending: "32,500 RSD",
    transactions: [
      { id: 1, date: "12.11.2024", type: "Priliv", description: "Mesečna uplata džeparca", amount: "5,000 RSD" },
      { id: 2, date: "05.11.2024", type: "Odliv", description: "Kupovina u prodavnici", amount: "-1,200 RSD" },
      { id: 3, date: "28.10.2024", type: "Odliv", description: "Frizerske usluge", amount: "-800 RSD" },
      { id: 4, date: "15.10.2024", type: "Priliv", description: "Mesečna uplata džeparca", amount: "5,000 RSD" },
      { id: 5, date: "02.10.2024", type: "Odliv", description: "Kupovina u prodavnici", amount: "-950 RSD" },
      { id: 6, date: "15.09.2024", type: "Priliv", description: "Mesečna uplata džeparca", amount: "5,000 RSD" }
    ]
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'aktivna': { 
        icon: CheckmarkFilled, 
        color: isDarkMode ? '#9fb746' : '#9fb746',
        bg: isDarkMode ? 'rgba(159, 183, 70, 0.15)' : 'rgba(159, 183, 70, 0.1)',
        text: 'Aktivna'
      },
      'kasni': { 
        icon: WarningFilled, 
        color: isDarkMode ? '#f59e0b' : '#f59e0b',
        bg: isDarkMode ? 'rgba(245, 158, 11, 0.15)' : 'rgba(245, 158, 11, 0.1)',
        text: 'Kasni'
      },
      'raskinuta': { 
        icon: CloseFilled, 
        color: isDarkMode ? '#ef4444' : '#ef4444',
        bg: isDarkMode ? 'rgba(239, 68, 68, 0.15)' : 'rgba(239, 68, 68, 0.1)',
        text: 'Raskinuta'
      },
      'plaćeno': { 
        icon: CheckmarkFilled, 
        color: isDarkMode ? '#9fb746' : '#9fb746',
        bg: isDarkMode ? 'rgba(159, 183, 70, 0.15)' : 'rgba(159, 183, 70, 0.1)',
        text: 'Plaćeno'
      }
    };

    const config = statusConfig[status.toLowerCase()] || statusConfig['aktivna'];
    const Icon = config.icon;

    return (
      <span style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.375rem',
        padding: '0.25rem 0.625rem',
        borderRadius: '9999px',
        fontSize: '0.8125rem',
        fontWeight: '500',
        color: config.color,
        backgroundColor: config.bg
      }}>
        <Icon size={14} />
        {config.text}
      </span>
    );
  };

  const getPackageIcon = (packageType) => {
    return <PackageIcon size={16} style={{ color: isDarkMode ? '#9fb746' : '#9fb746' }} />;
  };

  return (
    <PageContainer>
      {/* Header sa Nazad button-om i profilom */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
        <Button
          kind="ghost"
          size="sm"
          renderIcon={ArrowLeft}
          hasIconOnly
          iconDescription="Nazad"
          onClick={() => navigate(`/residents/${id}`)}
        />
        
        <div 
          style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '0.75rem',
            padding: '0.5rem 0.75rem',
            backgroundColor: isDarkMode ? '#2d2d2d' : 'white',
            borderRadius: '4px',
            border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
          onClick={() => navigate(`/residents/${id}`)}
          onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-1px)'}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
        >
          <div style={{
            width: '32px',
            height: '32px',
            borderRadius: '4px',
            overflow: 'hidden',
            flexShrink: 0,
            backgroundColor: 'rgba(159, 183, 70, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <UserAvatar size={20} style={{ color: '#9fb746' }} />
          </div>
          <div>
            <div style={{ 
              fontSize: '0.875rem', 
              fontWeight: '600',
              color: isDarkMode ? '#f4f4f4' : 'inherit'
            }}>
              {residentName}
            </div>
            <div style={{ 
              fontSize: '0.7rem', 
              opacity: 0.7,
              color: isDarkMode ? '#d0d0d0' : 'inherit'
            }}>
              💳 Finansije i ugovor
            </div>
          </div>
        </div>
      </div>

      {/* Contract Information */}
      <div>
        <Tile style={{ 
          marginBottom: '1rem',
          padding: '1.25rem'
        }}>
          <div 
            onClick={() => toggleSection('contractInfo')}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: expandedSections.contractInfo ? '0.75rem' : '0',
              cursor: 'pointer'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Document size={20} style={{ color: '#9fb746' }} />
              <h3 style={{ margin: 0, fontSize: '0.875rem', fontWeight: '600', color: isDarkMode ? '#f4f4f4' : 'inherit' }}>
                Informacije o ugovoru
              </h3>
            </div>
            {expandedSections.contractInfo ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </div>
          <div style={{
            maxHeight: expandedSections.contractInfo ? '2000px' : '0',
            opacity: expandedSections.contractInfo ? 1 : 0,
            overflow: 'hidden',
            transition: 'max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            ...(expandedSections.contractInfo ? {} : {
              transition: 'max-height 0.2s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.15s cubic-bezier(0.4, 0, 0.2, 1)'
            })
          }}>
            <div style={{ 
              display: 'flex',
              flexDirection: 'column',
              gap: '0.75rem'
            }}>
              {/* Contract number and package */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '0.625rem',
                backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                borderRadius: '4px',
                border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`
              }}>
                <span style={{ fontSize: '0.8125rem', opacity: 0.7 }}>Broj ugovora</span>
                <span style={{ fontSize: '0.8125rem', fontFamily: 'monospace', color: isDarkMode ? '#e8e8e8' : 'inherit' }}>
                  {contractData.contractNumber}
                </span>
              </div>

              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '0.625rem',
                backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                borderRadius: '4px',
                border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`
              }}>
                <span style={{ fontSize: '0.8125rem', opacity: 0.7 }}>Tip paketa</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                  <PackageIcon size={16} style={{ color: '#9fb746' }} />
                  <span style={{ fontSize: '0.8125rem', color: isDarkMode ? '#e8e8e8' : 'inherit' }}>
                    {contractData.packageType}
                  </span>
                </div>
              </div>

              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '0.625rem',
                backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                borderRadius: '4px',
                border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`
              }}>
                <span style={{ fontSize: '0.8125rem', opacity: 0.7 }}>Status uplate</span>
                {getStatusBadge(contractData.paymentStatus)}
              </div>

              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '0.625rem',
                backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                borderRadius: '4px',
                border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`
              }}>
                <span style={{ fontSize: '0.8125rem', opacity: 0.7 }}>Cena po danu</span>
                <span style={{ fontSize: '0.875rem', color: '#9fb746', fontWeight: '500' }}>
                  {contractData.pricePerDay}
                </span>
              </div>

              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '0.625rem',
                backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                borderRadius: '4px',
                border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`
              }}>
                <span style={{ fontSize: '0.8125rem', opacity: 0.7 }}>Cena po mesecu</span>
                <span style={{ fontSize: '0.875rem', color: '#9fb746', fontWeight: '500' }}>
                  {contractData.pricePerMonth}
                </span>
              </div>

              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '0.625rem',
                backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                borderRadius: '4px',
                border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`
              }}>
                <span style={{ fontSize: '0.8125rem', opacity: 0.7 }}>Sledeća uplata</span>
                <span style={{ fontSize: '0.8125rem', color: isDarkMode ? '#e8e8e8' : 'inherit' }}>
                  {contractData.nextPaymentDue}
                </span>
              </div>

              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '0.625rem',
                backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                borderRadius: '4px',
                border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`
              }}>
                <span style={{ fontSize: '0.8125rem', opacity: 0.7 }}>Period ugovora</span>
                <span style={{ fontSize: '0.8125rem', color: isDarkMode ? '#e8e8e8' : 'inherit' }}>
                  {contractData.startDate} — {contractData.endDate}
                </span>
              </div>

              {/* Package Description */}
              <div style={{
                padding: '0.75rem',
                backgroundColor: isDarkMode ? 'rgba(159, 183, 70, 0.08)' : 'rgba(159, 183, 70, 0.05)',
                borderRadius: '4px',
                border: `1px solid ${isDarkMode ? 'rgba(159, 183, 70, 0.15)' : 'rgba(159, 183, 70, 0.1)'}`,
                marginTop: '0.5rem'
              }}>
                <div style={{ 
                  fontSize: '0.75rem', 
                  opacity: 0.7,
                  marginBottom: '0.375rem'
                }}>
                  Opis paketa
                </div>
                <div style={{ 
                  fontSize: '0.8125rem',
                  color: isDarkMode ? '#e8e8e8' : 'inherit',
                  lineHeight: '1.5'
                }}>
                  {contractData.description}
                </div>
              </div>
            </div>
          </div>
        </Tile>
      </div>

      {/* Payment History */}
      <div>
        <Tile style={{ 
          marginBottom: '1rem',
          padding: '1.25rem'
        }}>
          <div 
            onClick={() => toggleSection('paymentHistory')}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: expandedSections.paymentHistory ? '0.75rem' : '0',
              cursor: 'pointer'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Currency size={20} style={{ color: '#9fb746' }} />
              <h3 style={{ margin: 0, fontSize: '0.875rem', fontWeight: '600', color: isDarkMode ? '#f4f4f4' : 'inherit' }}>
                Istorija uplata
              </h3>
            </div>
            {expandedSections.paymentHistory ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </div>
          <div style={{
            maxHeight: expandedSections.paymentHistory ? '3000px' : '0',
            opacity: expandedSections.paymentHistory ? 1 : 0,
            overflow: 'hidden',
            transition: 'max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            ...(expandedSections.paymentHistory ? {} : {
              transition: 'max-height 0.2s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.15s cubic-bezier(0.4, 0, 0.2, 1)'
            })
          }}>
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: '0.5rem'
            }}>
              {paymentHistory.map((payment) => (
                <div
                  key={payment.id}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '0.625rem',
                    backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                    borderRadius: '4px',
                    border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`,
                    gap: '1rem',
                    flexWrap: 'wrap'
                  }}
                >
                  <div style={{ display: 'flex', gap: '1rem', flex: '1 1 auto', flexWrap: 'wrap', alignItems: 'center' }}>
                    <div style={{ minWidth: '120px' }}>
                      <div style={{ fontSize: '0.8125rem', color: isDarkMode ? '#e8e8e8' : 'inherit' }}>
                        {payment.month}
                      </div>
                      <div style={{ fontSize: '0.75rem', opacity: 0.6 }}>
                        {payment.date}
                      </div>
                    </div>
                    <div style={{ minWidth: '100px' }}>
                      <div style={{ fontSize: '0.875rem', color: '#9fb746', fontWeight: '500' }}>
                        {payment.amount}
                      </div>
                    </div>
                    <div style={{ fontSize: '0.75rem', opacity: 0.7 }}>
                      {payment.method}
                    </div>
                  </div>
                  <div>
                    {getStatusBadge(payment.status)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Tile>
      </div>

      {/* Invoices */}
      <div>
        <Tile style={{ 
          marginBottom: '1rem',
          padding: '1.25rem'
        }}>
          <div 
            onClick={() => toggleSection('invoices')}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: expandedSections.invoices ? '0.75rem' : '0',
              cursor: 'pointer'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Document size={20} style={{ color: '#9fb746' }} />
              <h3 style={{ margin: 0, fontSize: '0.875rem', fontWeight: '600', color: isDarkMode ? '#f4f4f4' : 'inherit' }}>
                Fakture
              </h3>
            </div>
            {expandedSections.invoices ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </div>
          <div style={{
            maxHeight: expandedSections.invoices ? '3000px' : '0',
            opacity: expandedSections.invoices ? 1 : 0,
            overflow: 'hidden',
            transition: 'max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            ...(expandedSections.invoices ? {} : {
              transition: 'max-height 0.2s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.15s cubic-bezier(0.4, 0, 0.2, 1)'
            })
          }}>
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: '0.5rem'
            }}>
              {invoices.map((invoice) => (
                <div
                  key={invoice.id}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '0.625rem',
                    backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                    borderRadius: '4px',
                    border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`,
                    gap: '1rem',
                    flexWrap: 'wrap'
                  }}
                >
                  <div style={{ display: 'flex', gap: '1rem', flex: '1 1 auto', flexWrap: 'wrap', alignItems: 'center' }}>
                    <div style={{ minWidth: '140px' }}>
                      <div style={{ fontSize: '0.8125rem', fontFamily: 'monospace', color: isDarkMode ? '#e8e8e8' : 'inherit' }}>
                        {invoice.number}
                      </div>
                      <div style={{ fontSize: '0.75rem', opacity: 0.6 }}>
                        {invoice.period}
                      </div>
                    </div>
                    <div style={{ minWidth: '80px' }}>
                      <div style={{ fontSize: '0.75rem', opacity: 0.6 }}>
                        {invoice.date}
                      </div>
                    </div>
                    <div style={{ minWidth: '100px' }}>
                      <div style={{ fontSize: '0.875rem', color: '#9fb746', fontWeight: '500' }}>
                        {invoice.amount}
                      </div>
                    </div>
                    <div>
                      {getStatusBadge(invoice.status)}
                    </div>
                  </div>
                  <Button
                    kind="ghost"
                    size="sm"
                    renderIcon={Download}
                    onClick={(e) => {
                      e.stopPropagation();
                      console.log('Download invoice:', invoice.number);
                    }}
                  >
                    Preuzmi
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </Tile>
      </div>

      {/* Džeparac (Pocket Money) */}
      <div>
        <Tile style={{ 
          marginBottom: '1rem',
          padding: '1.25rem'
        }}>
          <div 
            onClick={() => toggleSection('pocketMoney')}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: expandedSections.pocketMoney ? '0.75rem' : '0',
              cursor: 'pointer'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Wallet size={20} style={{ color: '#9fb746' }} />
              <h3 style={{ margin: 0, fontSize: '0.875rem', fontWeight: '600', color: isDarkMode ? '#f4f4f4' : 'inherit' }}>
                Džeparac
              </h3>
            </div>
            {expandedSections.pocketMoney ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </div>
          <div style={{
            maxHeight: expandedSections.pocketMoney ? '3000px' : '0',
            opacity: expandedSections.pocketMoney ? 1 : 0,
            overflow: 'hidden',
            transition: 'max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            ...(expandedSections.pocketMoney ? {} : {
              transition: 'max-height 0.2s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.15s cubic-bezier(0.4, 0, 0.2, 1)'
            })
          }}>
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: '0.75rem'
            }}>
              {/* Balance summary cards */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                gap: '0.75rem'
              }}>
                <div style={{
                  padding: '1rem',
                  textAlign: 'center',
                  backgroundColor: isDarkMode ? 'rgba(159, 183, 70, 0.08)' : 'rgba(159, 183, 70, 0.05)',
                  borderRadius: '4px',
                  border: `1px solid ${isDarkMode ? 'rgba(159, 183, 70, 0.15)' : 'rgba(159, 183, 70, 0.1)'}`
                }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#9fb746' }}>
                    {pocketMoneyData.balance}
                  </div>
                  <div style={{ fontSize: '0.75rem', opacity: 0.7, marginTop: '0.25rem' }}>
                    Raspoloživi džeparac
                  </div>
                </div>
                <div style={{
                  padding: '1rem',
                  textAlign: 'center',
                  backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                  borderRadius: '4px',
                  border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`
                }}>
                  <div style={{ fontSize: '1.25rem', fontWeight: '600', color: '#9fb746' }}>
                    {pocketMoneyData.totalDeposits}
                  </div>
                  <div style={{ fontSize: '0.75rem', opacity: 0.7, marginTop: '0.25rem' }}>
                    Ukupne uplate
                  </div>
                </div>
                <div style={{
                  padding: '1rem',
                  textAlign: 'center',
                  backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                  borderRadius: '4px',
                  border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`
                }}>
                  <div style={{ fontSize: '1.25rem', fontWeight: '600', color: '#ef4444' }}>
                    {pocketMoneyData.totalSpending}
                  </div>
                  <div style={{ fontSize: '0.75rem', opacity: 0.7, marginTop: '0.25rem' }}>
                    Ukupne potrošnje
                  </div>
                </div>
              </div>

              {/* Transactions */}
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: '0.5rem'
              }}>
                {pocketMoneyData.transactions.map((tx) => (
                  <div
                    key={tx.id}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '0.625rem',
                      backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                      borderRadius: '4px',
                      border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`,
                      gap: '1rem',
                      flexWrap: 'wrap'
                    }}
                  >
                    <div style={{ display: 'flex', gap: '1rem', flex: '1 1 auto', flexWrap: 'wrap', alignItems: 'center' }}>
                      <div style={{ minWidth: '90px' }}>
                        <div style={{ fontSize: '0.75rem', opacity: 0.6 }}>
                          {tx.date}
                        </div>
                      </div>
                      <div style={{ flex: '1 1 auto', minWidth: '150px' }}>
                        <div style={{ fontSize: '0.8125rem', color: isDarkMode ? '#e8e8e8' : 'inherit' }}>
                          {tx.description}
                        </div>
                      </div>
                      <div style={{ minWidth: '90px', textAlign: 'right' }}>
                        <div style={{ 
                          fontSize: '0.875rem', 
                          fontWeight: '500',
                          color: tx.type === 'Priliv' ? '#9fb746' : '#ef4444'
                        }}>
                          {tx.amount}
                        </div>
                      </div>
                    </div>
                    <div>
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.375rem',
                        padding: '0.25rem 0.625rem',
                        borderRadius: '9999px',
                        fontSize: '0.8125rem',
                        fontWeight: '500',
                        color: tx.type === 'Priliv' ? '#9fb746' : '#ef4444',
                        backgroundColor: tx.type === 'Priliv' 
                          ? (isDarkMode ? 'rgba(159, 183, 70, 0.15)' : 'rgba(159, 183, 70, 0.1)')
                          : (isDarkMode ? 'rgba(239, 68, 68, 0.15)' : 'rgba(239, 68, 68, 0.1)')
                      }}>
                        {tx.type === 'Priliv' ? 'Priliv' : 'Odliv'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Tile>
      </div>
    </PageContainer>
  );
};

export default ResidentFinances;
